import React from "react";
import { useState, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  CircularProgress,
  TextField,
  Box,
  IconButton,
  Link as MuiLink,
} from "@mui/material";
import { Button as Btn } from "../../../components/atoms/Button";
import theme from "../../../theme";
import { FlexRow, Spacer } from "../../../components";
import { useSnackbar } from "../../../context/SnackbarContext";
import type { ColorsCodeType } from "../../../typings";
import { MdAttachFile, MdDelete, MdAdd } from "react-icons/md";
import { FaUpload, FaTrash } from "react-icons/fa";
import api from "../../../api/axiosInstance";

interface ExtraDocument {
  title: string;
  attachment_url: string[];
}

interface UploadExtraDocumentsModalProps {
  open: boolean;
  onClose: () => void;
  scholarshipApplicationId: number;
  onRefresh: () => void;
}

const uploadDocument = async (file: File): Promise<string> => {
  const formData = new FormData();
  formData.append("file", file);
  const response = await api.post(
    "/scholarship-applications/documents/",
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );
  return response.data.data.document_url;
};

export default function UploadExtraDocumentsModal({
  open,
  onClose,
  scholarshipApplicationId,
  onRefresh,
}: UploadExtraDocumentsModalProps) {
  const { showSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [documents, setDocuments] = useState<ExtraDocument[]>([
    { title: "", attachment_url: [] },
  ]);
  const [uploadingFiles, setUploadingFiles] = useState<{
    [docIndex: number]: { [fileIndex: number]: boolean };
  }>({});
  const fileInputRefs = useRef<{ [key: number]: HTMLInputElement | null }>({});

  const handleAddDocument = () => {
    setDocuments([...documents, { title: "", attachment_url: [] }]);
  };

  const handleRemoveDocument = (index: number) => {
    setDocuments(documents.filter((_, i) => i !== index));
    // Clean up uploading state
    const newUploadingFiles = { ...uploadingFiles };
    delete newUploadingFiles[index];
    setUploadingFiles(newUploadingFiles);
  };

  const handleTitleChange = (index: number, title: string) => {
    const newDocuments = [...documents];
    newDocuments[index].title = title;
    setDocuments(newDocuments);
  };

  const handleFileUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    docIndex: number
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Initialize uploading state for this document if needed
    if (!uploadingFiles[docIndex]) {
      setUploadingFiles({ ...uploadingFiles, [docIndex]: {} });
    }

    const fileIndex = documents[docIndex].attachment_url.length;
    setUploadingFiles((prev) => ({
      ...prev,
      [docIndex]: { ...prev[docIndex], [fileIndex]: true },
    }));

    try {
      const url = await uploadDocument(file);
      const newDocuments = [...documents];
      newDocuments[docIndex].attachment_url.push(url);
      setDocuments(newDocuments);
      showSnackbar("Document uploaded successfully", "success");
    } catch (error) {
      console.error("Error uploading document:", error);
      showSnackbar("Failed to upload document. Please try again.", "error");
    } finally {
      setUploadingFiles((prev) => {
        const newState = { ...prev };
        if (newState[docIndex]) {
          delete newState[docIndex][fileIndex];
        }
        return newState;
      });
    }
  };

  const handleRemoveFile = (docIndex: number, urlIndex: number) => {
    const newDocuments = [...documents];
    newDocuments[docIndex].attachment_url.splice(urlIndex, 1);
    setDocuments(newDocuments);
  };

  const handleSubmit = async () => {
    // Validate that at least one document has at least one file
    const hasValidDocuments = documents.some(
      (doc) => doc.attachment_url.length > 0
    );

    if (!hasValidDocuments) {
      showSnackbar(
        "Please upload at least one document file before submitting.",
        "warning"
      );
      return;
    }

    try {
      setLoading(true);

      // Prepare the payload according to API structure
      const extraDocuments = documents
        .filter((doc) => doc.attachment_url.length > 0)
        .map((doc) => {
          const payload: any = {
            attachment_url: doc.attachment_url,
          };
          // Only include title if it's provided
          if (doc.title.trim()) {
            payload.title = doc.title.trim();
          }
          return payload;
        });

      await api.post("/scholarship-applications/attach-extra-documents/", {
        scholarship_application_id: scholarshipApplicationId,
        extra_documents: extraDocuments,
      });

      showSnackbar("Extra documents attached successfully", "success");

      // Refresh the applications list
      onRefresh();

      // Reset form and close modal after successful submission
      setDocuments([{ title: "", attachment_url: [] }]);
      setUploadingFiles({});
      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (error: any) {
      console.error("Error attaching extra documents:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        "Failed to attach extra documents. Please try again.";
      showSnackbar(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setDocuments([{ title: "", attachment_url: [] }]);
      setUploadingFiles({});
      onClose();
    }
  };

  const isUploading = Object.values(uploadingFiles).some((docFiles) =>
    Object.values(docFiles).some((uploading) => uploading)
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{
        style: {
          borderRadius: "6px",
          maxWidth: "700px",
          width: "650px",
        },
      }}
    >
      <DialogTitle
        style={{
          padding: "24px 24px 16px",
          fontSize: "24px",
          fontWeight: 600,
        }}
      >
        <FlexRow align="center">
          <MdAttachFile
            size={24}
            style={{
              marginRight: "10px",
              color: theme.palette.primary.main,
            }}
          />
          Upload Additional Documents
        </FlexRow>
      </DialogTitle>
      <DialogContent style={{ padding: "0px 24px 16px", maxHeight: "60vh", overflowY: "auto" }}>
        <Typography
          variant="body2"
          style={{
            marginBottom: "20px",
            color: theme.palette.colors.gray["500"],
          }}
        >
          Upload additional documents for this scholarship application. You can
          add multiple documents, each with optional titles and multiple files.
        </Typography>

        {documents.map((document, docIndex) => (
          <Box
            key={docIndex}
            sx={{
              mb: 3,
              p: 2,
              border: `1px solid ${theme.palette.colors.gray["300"]}`,
              borderRadius: "8px",
              bgcolor: theme.palette.colors.white["400"],
              position: "relative",
            }}
          >
            {documents.length > 1 && (
              <IconButton
                onClick={() => handleRemoveDocument(docIndex)}
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  color: theme.palette.colors.red["500"],
                  "&:hover": {
                    backgroundColor: theme.palette.colors.red["50"],
                  },
                }}
                size="small"
              >
                <MdDelete size={18} />
              </IconButton>
            )}

            <Typography
              variant="subtitle2"
              sx={{
                color: theme.palette.primary.main,
                fontWeight: 600,
                mb: 2,
              }}
            >
              Document {docIndex + 1}
            </Typography>

            <TextField
              label="Document Title"
              value={document.title}
              onChange={(e) => handleTitleChange(docIndex, e.target.value)}
              fullWidth
              variant="outlined"
              placeholder="Enter document title..."
              sx={{
                marginBottom: "16px",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "4px",
                },
              }}
            />

            <Box sx={{ mb: 2 }}>
              <input
                ref={(el) => {
                  fileInputRefs.current[docIndex] = el;
                }}
                accept="application/pdf,image/*"
                id={`extra-document-upload-${docIndex}`}
                type="file"
                style={{ display: "none" }}
                onChange={(e) => handleFileUpload(e, docIndex)}
                disabled={loading || isUploading}
              />
              <Btn
                buttonColor={
                  theme.palette.colors.gray["600"] as ColorsCodeType
                }
                textColor={
                  theme.palette.colors.white["400"] as ColorsCodeType
                }
                onClick={() => {
                  fileInputRefs.current[docIndex]?.click();
                }}
                disabled={loading || isUploading}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <FaUpload size={14} />
                {uploadingFiles[docIndex] &&
                Object.values(uploadingFiles[docIndex]).some((u) => u)
                  ? "Uploading..."
                  : "Upload File"}
              </Btn>
            </Box>

            {document.attachment_url.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.colors.gray["600"],
                    mb: 1,
                    fontWeight: 500,
                  }}
                >
                  Uploaded Files:
                </Typography>
                {document.attachment_url.map((url, urlIndex) => (
                  <Box
                    key={urlIndex}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      p: 1,
                      mb: 0.5,
                      bgcolor: theme.palette.colors.gray["50"],
                      borderRadius: "4px",
                    }}
                  >
                    <MuiLink
                      href={url}
                      target="_blank"
                      rel="noopener"
                      variant="body2"
                      underline="hover"
                      sx={{
                        color: theme.palette.primary.main,
                        flex: 1,
                        minWidth: 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        mr: 1,
                      }}
                    >
                      {url.split("/").pop()}
                    </MuiLink>
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveFile(docIndex, urlIndex)}
                      disabled={loading || isUploading}
                      sx={{
                        color: theme.palette.colors.red["500"],
                        "&:hover": {
                          backgroundColor: theme.palette.colors.red["50"],
                        },
                      }}
                    >
                      <FaTrash size={12} />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        ))}

        <Btn
          buttonColor={theme.palette.colors.gray["600"] as ColorsCodeType}
          textColor={theme.palette.colors.white["400"] as ColorsCodeType}
          onClick={handleAddDocument}
          disabled={loading || isUploading}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            marginTop: "8px",
          }}
        >
          <MdAdd size={18} />
          Add Another Document
        </Btn>
      </DialogContent>
      <FlexRow
        justify="flex-end"
        align="center"
        style={{ padding: "15px 24px 24px" }}
      >
        <Btn
          buttonColor={
            theme.palette.colors["black"]["600"] as ColorsCodeType
          }
          textColor={theme.palette.colors["white"]["400"] as ColorsCodeType}
          onClick={handleClose}
          disabled={loading || isUploading}
        >
          Cancel
        </Btn>
        <Spacer horizontal={1} />
        <Btn
          onClick={handleSubmit}
          disabled={
            loading ||
            isUploading ||
            !documents.some((doc) => doc.attachment_url.length > 0)
          }
        >
          {loading ? (
            <>
              <CircularProgress
                size={16}
                style={{
                  marginRight: 8,
                  color: theme.palette.colors.white[400] as string,
                }}
              />
              Submitting...
            </>
          ) : (
            "Submit"
          )}
        </Btn>
      </FlexRow>
    </Dialog>
  );
}

